import Big from 'big.js'
import * as React from 'react'

import CategoryDefault from '@/assets/food/icons/categoryDefault.svg?react'
import StoreDefault from '@/assets/food/icons/storeDefault.svg?react'
import CartPlusIcon from '@/assets/icons/cart-plus.svg?react'
import ChevronLeft from '@/assets/icons/chevron-left.svg?react'
import type {
  Customization,
  MenuItemList,
  StoreIdProps,
} from '@/lib/food/foodModel'
import useFoodCartMutation, {FoodCartAction} from '@/lib/food/hooks/useFoodCartMutation'
import {StoreDetailRoute} from '@/routeRegistry'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import toastErrorMessage from '@/utils/errors/toastErrorMessage'

import CategoryItemPrice from './components/CategoryItemPrice'
import CustomizationUI from './components/CustomizeUI'
import style from './FoodStoreCategoryDetail.module.scss'

interface FoodStoreCategoryDetailProps {
  categoryDetail: MenuItemList
  storeId: StoreIdProps
}

type FlatSelectionMap = Record<string, number>

const DESCRIPTION_DEFAULT = 'Not available'

const ImageDefaultLoader = ({value}: {value: 'store' | 'category'}) => {
  return (
    <div className={style.StoreImageDefaultContainer}>
      {value === 'store' ? <StoreDefault /> : <CategoryDefault />}
    </div>
  )
}

export default memo(function FoodStoreCategoryDetail({
  categoryDetail,
  storeId,
}: FoodStoreCategoryDetailProps) {
  const {mutate: mutateFoodCart} = useFoodCartMutation()
  console.log(storeId)

  const [counter, setCounter] = useState(1)
  const [noteInput, setNoteInput] = useState('')
  const [selectionMap, setSelectionMap] = useState<Record<string, number>>({})

  const sortCustomizations = useCallback(
    (customizations: Customization[] = []): Customization[] => {
      return customizations
        .map(cust => ({
          ...cust,
          options: cust.options?.map(opt => ({
            ...opt,
            customizations: sortCustomizations(opt.customizations),
          })),
        }))
        .sort((a, b) => {
          const aIsRequired = a.min_choice_options > 0 ? 0 : 1
          const bIsRequired = b.min_choice_options > 0 ? 0 : 1
          return aIsRequired - bIsRequired
        })
    },
    [],
  )

  const sortedCustomizations = useMemo(
    () => sortCustomizations(categoryDetail.customizations),
    [categoryDetail.customizations, sortCustomizations],
  )

  const maxItemQuantityAvailable = categoryDetail.qty_available ?? 99

  const getInitialSelections = useCallback(
    (
      customizations: Customization[],
      selectionMap: Record<string, number> = {},
    ): Record<string, number> => {
      for (const customization of customizations) {
        const requiredQty = customization.min_choice_options
        const options = customization.options ?? []

        const sortedOptions = [...options].sort((a, b) => a.price - b.price)

        for (let i = 0; i < requiredQty && i < sortedOptions.length; i++) {
          const option = sortedOptions[i]
          if (option?.option_id !== undefined) {
            selectionMap[String(option.option_id)] = 1
          }

          if (option?.customizations?.length) {
            getInitialSelections(option.customizations, selectionMap)
          }
        }
      }

      return selectionMap
    },
    [],
  )

  useEffect(() => {
    const initial = getInitialSelections(categoryDetail.customizations ?? [])
    setSelectionMap(initial)
  }, [categoryDetail.customizations, getInitialSelections])

  const handleCounterAdding = () => {
    if (counter === maxItemQuantityAvailable) {
      return
    }
    setCounter(counter + 1)
  }
  const handleCounterRemoving = () => {
    if (counter === 0) {
      return
    }
    setCounter(counter - 1)
  }

  const calculateTotalPrice = (
    customization: Customization,
    selectionMap: Record<string, number>,
  ): Big => {
    let total = new Big(0)

    customization.options?.forEach(option => {
      const qty = selectionMap[option.option_id] ?? 0

      if (qty > 0) {
        total = total.plus(new Big(option.price).times(qty))

        option.customizations?.forEach(nested => {
          total = total.plus(calculateTotalPrice(nested, selectionMap))
        })
      }
    })

    return total
  }

  const getTotalPrice = (
    basePrice: number,
    customizations: Customization[],
    selectionMap: Record<string, number>,
  ): string => {
    const baseProductPriceWithQuantity = basePrice

    let total = new Big(baseProductPriceWithQuantity)

    customizations.forEach(customization => {
      total = total.plus(calculateTotalPrice(customization, selectionMap))
    })

    return total.times(counter).div(100).toFixed(2)
  }

  const total = getTotalPrice(
    categoryDetail.price,
    categoryDetail.customizations ?? [],
    selectionMap,
  )

  const validateCustomizationWithErrors = (
    customization: Customization,
    selectionMap: FlatSelectionMap,
  ): string[] => {
    const errors: string[] = []

    const selectedQty =
      customization.options?.reduce((sum, option) => {
        const qty = selectionMap[option.option_id] ?? 0
        return sum + qty
      }, 0) ?? 0

    if (selectedQty < customization.min_choice_options) {
      errors.push(
        `Please select at least ${customization.min_choice_options} option(s) for "${customization.name}"`,
      )
    }

    if (customization.max_choice_options && selectedQty > customization.max_choice_options) {
      errors.push(
        `Please select no more than ${customization.max_choice_options} option(s) for "${customization.name}"`,
      )
    }

    for (const option of customization.options ?? []) {
      const qty = selectionMap[option.option_id] ?? 0

      if (qty > 0 && option.customizations?.length) {
        for (const nested of option.customizations) {
          const nestedErrors = validateCustomizationWithErrors(nested, selectionMap)
          errors.push(...nestedErrors)
        }
      }
    }

    return errors
  }

  const validateAllCustomizationsWithErrors = (
    customizations: Customization[],
    selectionMap: FlatSelectionMap,
  ): string[] => {
    return customizations.flatMap(c => validateCustomizationWithErrors(c, selectionMap))
  }

  //This function will return needed object structure for AddToCart feature
  const handleSubmit = () => {
    if (!categoryDetail.customizations) {
      console.error('Missing customization details')
      return
    }

    const errors = validateAllCustomizationsWithErrors(categoryDetail.customizations, selectionMap)

    if (errors.length > 0) {
      errors.forEach(error => {
        toastErrorMessage(error)
      })
      return
    }

    const selected = Object.entries(selectionMap).map(([optionId, qty]) => ({
      optionId,
      quantity: qty,
    }))

    console.log(selected)

    mutateFoodCart({
      action: FoodCartAction.Add,
      data: {
        item: {
          productId: categoryDetail.product_id,
          storeId: storeId.id,
          selectedOptions: selected,
          notes: noteInput,
        },
        quantity: counter,
      },
    })
  }

  const storeDetailParams = {
    id: storeId.id,
    store_name: storeId.store_name,
    store_description: storeId.store_description,
    store_rating: storeId.store_rating,
    store_rating_count: storeId.store_rating_count,
    store_address: storeId.store_address,
    store_cuisine: storeId.store_cuisine,
    store_delivery_time: storeId.store_delivery_time,
    store_delivery_distance: storeId.store_delivery_distance,
    store_image: storeId.store_image,
    lat: storeId.lat,
    lng: storeId.lng,
    address: storeId.address,
    user_street_num: storeId.user_street_num,
    user_street_name: storeId.user_street_name,
    user_city: storeId.user_city,
    user_state: storeId.user_state,
    user_zipcode: storeId.user_zipcode,
    user_country: storeId.user_country,
    pickup_enabled: storeId.pickup_enabled,
    delivery_enabled: storeId.delivery_enabled,
  }

  return (
    <div>
      <main className={style.PageContainer}>
        <section className={style.PageHeader}>
          <div className={style.PageHeaderContent}>
            <Link
              to={StoreDetailRoute.fullPath}
              aria-label='Go back to store detail page'
              params={{
                storeId: storeDetailParams,
              }}
              className={style.ButtonBack}
            >
              <ChevronLeft className={style.ButtonBackIcon} />
              <p className={style.ButtonBackTitleMobile}>Go back</p>
            </Link>
            <div className={style.HeaderImageContainer}>
              {categoryDetail.image ? (
                <img
                  src={categoryDetail.image}
                  alt=''
                  loading='lazy'
                  className={style.HeaderImage}
                />
              ) : (
                <ImageDefaultLoader value='category' />
              )}
            </div>
            <div className={style.HeaderInfoContainer}>
              <div className={style.HeaderInfoContent}>
                <h1 className={style.Heading}>{categoryDetail.name}</h1>
                <p className={style.Description}>
                  <VisuallyHidden>
                    Description: {categoryDetail.description.length === 0 && DESCRIPTION_DEFAULT}
                  </VisuallyHidden>
                  {categoryDetail.description}
                </p>
              </div>
              <div className={style.HeaderInfoBreak}></div>
              <div className={style.HeaderInfoController}>
                <div className={style.CounterContainer}>
                  <div className={style.CounterHeading}>Quantity</div>
                  <div className={style.CounterContent}>
                    <button
                      className={style.CounterButton}
                      type='button'
                      onClick={handleCounterRemoving}
                      aria-label='Decrease item quantity by one'
                    >
                      -
                    </button>
                    <div className={style.CounterValue}>{counter}</div>
                    <button
                      className={style.CounterButton}
                      type='button'
                      onClick={handleCounterAdding}
                      aria-label='Increase item quantity by one'
                    >
                      +
                    </button>
                  </div>
                </div>
                <button
                  type='button'
                  onClick={handleSubmit}
                  className={style.CartButton}
                  disabled={counter === 0}
                >
                  <CartPlusIcon className={style.CartIcon} />
                  <div className={style.CartValue}>
                    Add item to cart - <CategoryItemPrice price={total} />
                  </div>
                </button>
              </div>
            </div>
          </div>
        </section>
        <section className={style.PageContent}>
          <div className={style.NoteContainer}>
            <h2 className={style.NoteHeading}>Add your note here</h2>
            <textarea
              rows={4}
              className={style.NoteInput}
              placeholder=''
              value={noteInput}
              onChange={e => {
                setNoteInput(e.target.value)
              }}
              aria-placeholder='Add your note here'
            />
          </div>
          <div className={style.CategoryList} role='list'>
            {sortedCustomizations.map((customize, index) => (
              <CustomizationUI
                key={index}
                customization={customize}
                selectionMap={selectionMap}
                setSelectionMap={setSelectionMap}
              />
            ))}
          </div>
        </section>
        <div className={style.MobileController}>
          <div className={style.CounterContainer}>
            <h2 className={style.CounterHeading}>Quantity</h2>
            <div className={style.CounterContent}>
              <button
                className={style.CounterButton}
                type='button'
                onClick={handleCounterRemoving}
                aria-label='Descrease item quantity by one'
              >
                -
              </button>
              <div className={style.CounterValue}>{counter}</div>
              <button
                className={style.CounterButton}
                type='button'
                onClick={handleCounterAdding}
                aria-label='Increase item quantity by one'
              >
                +
              </button>
            </div>
          </div>
          <button
            type='button'
            disabled={counter === 0}
            className={style.CartButton}
            onClick={handleSubmit}
          >
            <CartPlusIcon className={style.CartIcon} />
            <div className={style.CartValue}>
              Add item to cart - <CategoryItemPrice price={total} />
            </div>
          </button>
        </div>
      </main>
    </div>
  )
})
